import { get, writable } from "svelte/store";
import { appManager } from "../../AppManager";
import { SafeToSchoolMapManager } from "./safe_to_school/SafeToSchoolMapManager";
import * as turf from "@turf/turf";
import { DockingStationsMapManager } from "./docking_stations/DockingStationsMapManager";
/**
 * Renders all layers on the map.
 */
export class ModelViewMapManager {
    constructor(modelViewManager) {
        this.maxWidth = 20;
        this.minWidth = 3;
        this.locationLayerId = "relevant-locations";
        this.locationLabelsLayerId = "relevant-locations-label";
        this.modelSourceId = "model";
        this.defaultVehicleTypeSettings = [
            {
                color: "#FFFFFF",
                maxTripCount: 10
            },
            {
                color: "#F00",
                maxTripCount: 10
            },
            {
                color: "#00F",
                maxTripCount: 100
            },
            {
                color: "#00B3FF",
                maxTripCount: 100
            },
            {
                color: "#0F0",
                maxTripCount: 100
            }
        ];
        this.vehicleTypeSettings = writable(this.defaultVehicleTypeSettings);
        this.minZoomTrips = 10;
        /**
         * When higher zoom, labels are visible.
         */
        this.MIN_LABELS_ZOOM = 13;
        this.MIN_HOVER_LEVEL = 12;
        this.SelectedMinWidth = 4;
        this.modelViewManager = modelViewManager;
        this.translationKeyStore = this.modelViewManager.getModelTranslationKeyStore();
        this.showCyclingScoreStore = this.modelViewManager.getShowCyclingScoreStore();
        this.showRelevantLocationsStore = this.modelViewManager.getShowRelevantLocationsStore();
        this.layers = this.modelViewManager.getLayersAndStatusStore();
        this.selectionStore = this.modelViewManager.getSelectionStore();
        this.tripsFilterStore = this.modelViewManager.getTripsFilterStore();
        this.locationsFilterStore = this.modelViewManager.getLocationsFilterStore();
        this.modelDataStore = this.modelViewManager.getModelDataStore();
        this.tripsFilterStore.subscribe(() => {
            if (typeof this.map === "undefined")
                return; // don't filter if map is not set yet.
            this.setVehicleTypeLayerFilters();
        });
        this.tripsFilterStore.subscribe(() => {
            if (typeof this.map === "undefined")
                return; // don't filter if map is not set yet.
            this.setVehicleTypeLayerFilters();
        });
        this.locationsFilterStore.subscribe(() => {
            if (typeof this.map === "undefined")
                return; // don't filter if map is not set yet.
            this.setVehicleTypeLayerFilters();
        });
        this.vehicleTypeSettings.subscribe(() => {
            if (typeof this.map === "undefined")
                return; // don't filter if map is not set yet.
            this.setVehicleTypeLayerFilters();
        });
        this.showCyclingScoreStore.subscribe(() => {
            if (typeof this.map === "undefined")
                return; // don't filter if map is not set yet.
            this.setVehicleTypeLayerFilters();
        });
        this.showRelevantLocationsStore.subscribe(() => {
            if (typeof this.map === "undefined")
                return; // don't filter if map is not set yet.
            this.setVehicleTypeLayerFilters();
        });
        this.layers.subscribe(ls => {
            if (typeof this.map === "undefined")
                return; // don't filter if map is not set yet.
            ls.forEach(l => {
                l.visible.subscribe(() => {
                    this.setVehicleTypeLayerFilters();
                });
            });
            this.setVehicleTypeLayerFilters();
        });
        this.modelDataStore.subscribe(model => {
            var _a, _b, _c, _d, _e, _f;
            if (typeof model === "undefined")
                return;
            const minZoomTrips = Number((_a = model.properties) === null || _a === void 0 ? void 0 : _a.minzoom_trips);
            if (!isNaN(minZoomTrips)) {
                this.minZoomTrips = minZoomTrips;
            }
            const value = (_b = model.properties.localization_key) !== null && _b !== void 0 ? _b : "";
            const type = (_c = model.properties["type"]) !== null && _c !== void 0 ? _c : "";
            const vehicleTypeSettings = [...this.defaultVehicleTypeSettings];
            if (type == "docking_stations") {
                this.modelCustomMapManager = new DockingStationsMapManager(this);
                const settings = this.modelCustomMapManager.getVehicleTypeSettings();
                if (settings.length > 0) {
                    settings.forEach(s => {
                        vehicleTypeSettings[s.vehicleType] = s;
                    });
                }
            }
            else if (value === "safe_to_school" ||
                value == "train_stations") {
                this.modelCustomMapManager = new SafeToSchoolMapManager(this);
                const settings = this.modelCustomMapManager.getVehicleTypeSettings();
                if (settings.length > 0) {
                    settings.forEach(s => {
                        vehicleTypeSettings[s.vehicleType] = s;
                    });
                }
            }
            else {
                const maxBicycle = Number((_d = model.properties) === null || _d === void 0 ? void 0 : _d.max_trip_count_bicycle);
                if (!isNaN(maxBicycle)) {
                    vehicleTypeSettings[2].maxTripCount = maxBicycle;
                }
                const maxCar = Number((_e = model.properties) === null || _e === void 0 ? void 0 : _e.max_trip_count_car);
                if (!isNaN(maxCar)) {
                    vehicleTypeSettings[1].maxTripCount = maxCar;
                }
                const maxPedestrian = Number((_f = model.properties) === null || _f === void 0 ? void 0 : _f.max_trip_count_pedestrian);
                if (!isNaN(maxPedestrian)) {
                    vehicleTypeSettings[4].maxTripCount = maxPedestrian;
                }
                console.log(vehicleTypeSettings);
            }
            this.vehicleTypeSettings.set(vehicleTypeSettings);
        });
    }
    initialize(map) {
        this.map = map;
        // hook up events.
        this.map.on("load", () => this._onMapLoad());
        this.map.on("click", (e) => this._onMapClick(e));
        this.map.on("mousemove", (e) => this._onMapMouseMove(e));
        this.map.on("zoomend", () => this.modelViewManager.onMapViewOrDataChanged());
        this.map.on("moveend", () => this.modelViewManager.onMapViewOrDataChanged());
        this.map.on("sourcedata", () => this.modelViewManager.onMapViewOrDataChanged());
    }
    getLocationsInView() {
        const layer = this.map.getLayer(this.locationLayerId);
        if (typeof layer === "undefined")
            return [];
        const features = this.map.queryRenderedFeatures(undefined, { layers: [this.locationLayerId] });
        const locationIds = [];
        features.forEach(f => {
            const locationId = Number(f.properties["_location_id"]);
            if (typeof locationId === "undefined")
                return [];
            locationIds.push(locationId);
        });
        return locationIds;
    }
    zoomToLineString(lineString, zoomWhenInBox) {
        const center = turf.center(turf.feature(lineString));
        const minZoom = 16;
        if (!zoomWhenInBox) {
            const mapZoom = this.map.getZoom();
            if (minZoom - mapZoom < 4) {
                const mapBbox = this.map.getBounds();
                const bboxGeometry = turf.bboxPolygon([mapBbox.getWest(), mapBbox.getSouth(), mapBbox.getEast(), mapBbox.getNorth()]);
                if (turf.booleanPointInPolygon(center.geometry, bboxGeometry)) {
                    return;
                }
            }
        }
        this.map.flyTo({
            center: { lng: center.geometry.coordinates[0], lat: center.geometry.coordinates[1] },
            zoom: minZoom,
            essential: true // this animation is considered essential with respect to prefers-reduced-motion
        });
    }
    zoomTo(loc, zoomWhenInBox) {
        if (!zoomWhenInBox) {
            const mapZoom = this.map.getZoom();
            if (loc.zoom - mapZoom < 4) {
                const mapBbox = this.map.getBounds();
                const bboxGeometry = turf.bboxPolygon([mapBbox.getWest(), mapBbox.getSouth(), mapBbox.getEast(), mapBbox.getNorth()]);
                if (turf.booleanPointInPolygon(turf.point([loc.center.lng, loc.center.lat]), bboxGeometry)) {
                    return;
                }
            }
        }
        this.map.flyTo({
            center: loc.center,
            zoom: loc.zoom,
            essential: true // this animation is considered essential with respect to prefers-reduced-motion
        });
    }
    zoomToExtent(polygon) {
        const bbox = turf.bbox(turf.feature(polygon));
        const center = this.map.getCenter();
        const bboxGeometry = turf.bboxPolygon(bbox);
        if (turf.booleanPointInPolygon(turf.point([center.lng, center.lat]), bboxGeometry)) {
            return;
        }
        this.map.fitBounds([[bbox[0], bbox[1]], [bbox[2], bbox[3]]], {
            animate: false
        });
    }
    setMinZoom(minZoom) {
        this.map.setMinZoom(minZoom);
    }
    _onMapMouseMove(e) {
    }
    waitStyleLoaded() {
        let resolve;
        const waiting = () => {
            if (!this.map.isStyleLoaded()) {
                resolve();
                // setTimeout(waiting, 500);
            }
            else {
                resolve();
            }
        };
        return new Promise((r, _) => {
            resolve = r;
            waiting();
        });
    }
    _onMapLoad() {
        const waiting = () => {
            if (!this.map.isStyleLoaded()) {
                setTimeout(waiting, 200);
            }
            else {
                this.addLayers();
            }
        };
        waiting();
    }
    _onMapClick(e) {
        e.preventDefault();
        // set bbox as 5px reactangle area around clicked point
        const bbox = [
            [e.point.x - 10, e.point.y - 10],
            [e.point.x + 10, e.point.y + 10]
        ];
        // check if there is a location in range around the clicked location.
        let selectedLocation = undefined;
        if (this.map.getLayoutProperty(this.locationLayerId, "visibility") !== "none") {
            const features = this.map.queryRenderedFeatures(bbox, { layers: [this.locationLayerId] });
            if (features && features.length > 0) {
                console.log(features[0]);
                selectedLocation = Number(features[0].properties["_location_id"]);
                if (isNaN(selectedLocation))
                    selectedLocation = undefined;
            }
        }
        // check if there is a link in range around the clicked location.
        let selectedSegment = undefined;
        if (typeof selectedLocation === "undefined") {
            const layers = [];
            for (let vehicleType = 0; vehicleType < get(this.vehicleTypeSettings).length; vehicleType++) {
                layers.push(`vehicle_${vehicleType}_trips`);
            }
            const features = this.map.queryRenderedFeatures(bbox, { layers: layers });
            if (features && features.length > 0) {
                const id = Number(features[0].properties["_segment_id"]);
                const forward = features[0].properties["_forward"];
                if (typeof id !== "undefined") {
                    selectedSegment = { id: id, forward: forward };
                }
            }
        }
        if (typeof selectedLocation !== "undefined") {
            this.selectionStore.set({ locationId: selectedLocation });
        }
        else if (typeof selectedSegment !== "undefined") {
            if (selectedSegment.forward) {
                this.selectionStore.set({ segmentId: selectedSegment.id });
            }
            else {
                this.selectionStore.set({ segmentId: -selectedSegment.id });
            }
        }
        else {
            this.selectionStore.set({});
        }
    }
    setVehicleTypeLayerFilters() {
        // check if layers are already setup, if no, do nothing.
        const layerNameVehicle0 = `vehicle_${0}_trips`;
        if (typeof this.map.getLayer(layerNameVehicle0) === "undefined")
            return;
        const layers = get(this.layers);
        const filteredTrips = get(this.tripsFilterStore);
        const locations = get(this.locationsFilterStore);
        const showCyclingScore = get(this.showCyclingScoreStore);
        const showRelevantLocations = get(this.showRelevantLocationsStore);
        const vehicleTypeSettings = get(this.vehicleTypeSettings);
        for (let vehicleType = 0; vehicleType < vehicleTypeSettings.length; vehicleType++) {
            const layerName = `vehicle_${vehicleType}_trips`;
            if (typeof this.map.getLayer(layerName) === "undefined")
                continue;
            const vehicleTypeSetting = vehicleTypeSettings[vehicleType];
            let calculateCount = ["+"];
            if (typeof filteredTrips === "undefined") {
                for (const layer of layers) {
                    if (!layer.vehicle)
                        continue;
                    if (layer.vehicle.id !== vehicleType)
                        continue;
                    if (!get(layer.visible))
                        continue;
                    if (!get(layer.enabled))
                        continue;
                    calculateCount.push(["case", ["has", `_layer_${layer.layer.id}`], ["get", `_layer_${layer.layer.id}`], 0]);
                }
            }
            else {
                for (const layer of layers) {
                    if (!layer.vehicle)
                        continue;
                    if (layer.vehicle.id !== vehicleType)
                        continue;
                    if (!get(layer.visible))
                        continue;
                    if (!get(layer.enabled))
                        continue;
                    const tripsPerLayer = filteredTrips.find(x => x.layerId == layer.layer.id);
                    if (typeof tripsPerLayer === "undefined")
                        continue;
                    for (const trip of tripsPerLayer.trips) {
                        calculateCount.push(["case", ["has", `_trip_${trip}`], 1, 0]);
                    }
                }
            }
            if (calculateCount.length === 1) {
                calculateCount = ["literal", 0];
            }
            const tripCountFactor = vehicleTypeSetting.maxTripCount / 20;
            const textField = ["let",
                "count",
                calculateCount,
                ["number-format", ["var", "count"], { "min-fraction-digits": 0, "max-fraction-digits": 0 }]];
            const countFilter = ["let",
                "count",
                calculateCount,
                [">", ["var", "count"], 0]];
            const getCount = ["var", "count"];
            const lineWidth = [
                "let", "count", calculateCount,
                ["interpolate", ["linear"], ["zoom"],
                    8, ["/", getCount, 100],
                    10, ["min", ["/", getCount, tripCountFactor * 2], this.maxWidth / 4],
                    12, ["min", ["/", getCount, tripCountFactor * 2], this.maxWidth / 4],
                    14, ["max", ["min", ["/", getCount, tripCountFactor], this.maxWidth / 2], this.minWidth],
                    16, ["max", ["min", ["/", getCount, tripCountFactor], this.maxWidth], this.minWidth]]
            ];
            const casingMin = 1;
            const shadowLineWidth = [
                "let", "count", calculateCount,
                ["interpolate", ["linear"], ["zoom"],
                    8, 0,
                    10, ["+", ["min", ["/", getCount, tripCountFactor * 2], this.maxWidth / 4], 1],
                    12, ["+", ["min", ["/", getCount, tripCountFactor * 2], this.maxWidth / 4], 1],
                    14, ["interpolate", ["linear"], ["max", ["min", ["/", getCount, tripCountFactor], this.maxWidth / 2], this.minWidth],
                        1, ["+", ["max", ["min", ["/", getCount, tripCountFactor], this.maxWidth / 2], this.minWidth], casingMin + 0.5],
                        this.maxWidth / 2, ["+", ["max", ["min", ["/", getCount, tripCountFactor], this.maxWidth / 2], this.minWidth], casingMin + 1.5]
                    ],
                    16, ["interpolate", ["linear"], ["max", ["min", ["/", getCount, tripCountFactor], this.maxWidth], this.minWidth],
                        1, ["+", ["max", ["min", ["/", getCount, tripCountFactor], this.maxWidth], this.minWidth], casingMin + 0.5],
                        this.maxWidth, ["+", ["max", ["min", ["/", getCount, tripCountFactor], this.maxWidth], this.minWidth], casingMin + 2.5]
                    ]
                ]
            ];
            // line shadow layer.    
            this.map.setFilter(`${layerName}-shadow`, countFilter);
            this.map.setPaintProperty(`${layerName}-shadow`, "line-width", shadowLineWidth);
            this.map.setLayerZoomRange(`${layerName}-shadow`, this.minZoomTrips, 24);
            // line layer.
            this.map.setFilter(layerName, countFilter);
            this.map.setLayerZoomRange(layerName, this.minZoomTrips, 24);
            this.map.setPaintProperty(layerName, "line-width", lineWidth);
            if (showCyclingScore) {
                console.log("showCyclingScore");
                const score = ["case", ["has", "_forward"],
                    ["case", ["get", "_forward"],
                        ["case", ["has", "_score:bicycle:safety:forward"],
                            ["get", "_score:bicycle:safety:forward"], ["literal", 1]],
                        ["case", ["has", "_score:bicycle:safety:backward"],
                            ["get", "_score:bicycle:safety:backward"], ["literal", 1]]], ["literal", 1]];
                const colorExpression = [
                    "interpolate",
                    ["linear"],
                    ["to-number", score, 0],
                    0,
                    "#000000",
                    0.01,
                    "#000000",
                    0.6,
                    "#000000",
                    0.601,
                    "#ff0000",
                    0.77,
                    "#ff0000",
                    0.771,
                    "#cccccc",
                    1,
                    "#cccccc",
                    1.01,
                    "#ffff00",
                    1.5,
                    "#ffff00"
                ];
                this.map.setPaintProperty(layerName, "line-color", colorExpression);
                this.map.setLayoutProperty(layerName, "line-sort-key", ["to-number", score, 0]);
            }
            else {
                this.map.setPaintProperty(layerName, "line-color", vehicleTypeSetting.color);
                this.map.setLayoutProperty(layerName, "line-sort-key", undefined);
            }
            // count label layer.
            this.map.setFilter(`${layerName}-counts`, countFilter);
            this.map.setLayoutProperty(`${layerName}-counts`, "text-field", textField);
            this.map.setLayoutProperty(`${layerName}-counts`, "symbol-sort-key", [
                "let", "count", calculateCount, ["-", ["to-number", getCount]]
            ]);
            // locations.
            if (typeof this.modelCustomMapManager !== "undefined" &&
                this.modelCustomMapManager.hasLocationLayer()) {
                this.modelCustomMapManager.setLocationLayerVehicleTypeLayerFilters(showCyclingScore, showRelevantLocations);
            }
            else {
                if (!showRelevantLocations) {
                    this.map.setLayoutProperty(this.locationLayerId, "visibility", "none");
                    this.map.setLayoutProperty(this.locationLayerId + "-back", "visibility", "none");
                    this.map.setLayoutProperty(this.locationLabelsLayerId, "visibility", "none");
                }
                else {
                    this.map.setLayoutProperty(this.locationLayerId, "visibility", "visible");
                    this.map.setLayoutProperty(this.locationLayerId + "-back", "visibility", "visible");
                    this.map.setLayoutProperty(this.locationLabelsLayerId, "visibility", "visible");
                }
            }
        }
        if (typeof this.modelCustomMapManager !== "undefined" &&
            this.modelCustomMapManager.hasCustomSelectionFilters()) {
            this.modelCustomMapManager.setCustomSelectionFilters(locations);
        }
        else {
            if (typeof locations === "undefined" || locations.length == 0) {
                this.map.setFilter(this.locationLayerId, ["==", "type", "primary"]);
                this.map.setFilter(this.locationLayerId + "-back", ["==", "type", "primary"]);
                this.map.setFilter(this.locationLabelsLayerId, ["==", "type", "primary"]);
            }
            else {
                const filter = ["any"];
                locations.forEach(l => {
                    filter.push(["==", "_location_id", l]);
                });
                this.map.setFilter(this.locationLayerId, filter);
                this.map.setFilter(this.locationLayerId + "-back", filter);
                this.map.setFilter(this.locationLabelsLayerId, filter);
            }
        }
    }
    addLayers() {
        const globalModelId = this.modelViewManager.getModelGlobalId();
        if (typeof this.map === "undefined")
            return; // the map is not initialized yet, this will be called again.
        if (typeof globalModelId === "undefined")
            return; // no model global id, the map was first.
        if (typeof this.map.getSource(this.modelSourceId) === "undefined") {
            this.map.addSource(this.modelSourceId, {
                type: "vector",
                url: appManager.api.trips.MvtJsonUrlForModelId(globalModelId)
            });
            this.addLocationLayers();
            const vehicleTypeSettings = get(this.vehicleTypeSettings);
            for (let vehicleType = 0; vehicleType < vehicleTypeSettings.length; vehicleType++) {
                this.addVehicleTypeTripShadowLayers(vehicleType);
            }
            for (let vehicleType = 0; vehicleType < vehicleTypeSettings.length; vehicleType++) {
                this.addVehicleTypeTripLayers(vehicleType);
            }
        }
        this.setVehicleTypeLayerFilters();
    }
    addLocationLayers() {
        if (typeof this.map.getLayer(this.locationLayerId + "-hover") !== "undefined")
            return;
        // get lowest label and road.
        const style = this.map.getStyle();
        let lowest = undefined;
        for (let lIdx = 0; lIdx < style.layers.length; lIdx++) {
            const layerStyle = style.layers[lIdx];
            if (layerStyle && layerStyle["source"] === "od-points") {
                if (!lowest) {
                    lowest = layerStyle.id;
                }
            }
            if (layerStyle && layerStyle["source-layer"] === "transportation_name") {
                if (!lowest) {
                    lowest = layerStyle.id;
                }
            }
        }
        this.map.addLayer({
            "id": this.locationLayerId + "-back",
            "type": "circle",
            "source": this.modelSourceId,
            "source-layer": "locations",
            "layout": {
                "visibility": "visible"
            },
            "paint": {
                "circle-color": "#000",
                "circle-radius": [
                    "interpolate", ["linear"], ["zoom"],
                    5, 4,
                    15, 10
                ]
            },
            "filter": ["literal", false]
        }, lowest);
        this.map.addLayer({
            "id": this.locationLayerId,
            "type": "circle",
            "source": this.modelSourceId,
            "source-layer": "locations",
            "layout": {
                "visibility": "visible",
                "circle-sort-key": ["literal", 0],
            },
            "paint": {
                "circle-color": "#FFF",
                "circle-radius": [
                    "interpolate", ["linear"], ["zoom"],
                    5, 3,
                    15, 9
                ],
            },
            "filter": ["literal", false]
        }, lowest);
        this.map.addLayer({
            "id": this.locationLabelsLayerId,
            "type": "symbol",
            "source": this.modelSourceId,
            "minzoom": this.MIN_LABELS_ZOOM,
            "source-layer": "locations",
            "layout": {
                "visibility": "visible",
                "text-field": "{name}",
                "text-size": 12,
                "text-anchor": "top",
                "text-offset": [0, 1]
            },
            "paint": {
                "text-color": "#000",
                "text-halo-color": "#ffffff",
                "text-halo-width": 2,
                "text-opacity": 1
            },
            "filter": ["literal", false]
        });
    }
    addVehicleTypeTripShadowLayers(vehicleType) {
        const layerName = `vehicle_${vehicleType}_trips`;
        if (typeof this.map.getLayer(layerName + "-shadow") !== "undefined")
            return;
        // get lowest label and road.
        const style = this.map.getStyle();
        let lowest = undefined;
        for (let lIdx = 0; lIdx < style.layers.length; lIdx++) {
            const layerStyle = style.layers[lIdx];
            if (layerStyle && layerStyle["source"] === "od-points") {
                if (!lowest) {
                    lowest = layerStyle.id;
                }
            }
            if (layerStyle && layerStyle["source-layer"] === "transportation_name") {
                if (!lowest) {
                    lowest = layerStyle.id;
                }
            }
            if (layerStyle && layerStyle["source-layer"] === "locations") {
                if (!lowest) {
                    lowest = layerStyle.id;
                }
            }
        }
        const offsetExpression = [
            "interpolate", ["linear"], ["zoom"],
            13, 0,
            14, 0,
            15, 0,
            16, 0,
            17, ["case", ["get", "_forward"], 12, -12],
            18, ["case", ["get", "_forward"], 14, -14],
        ];
        this.map.addLayer({
            "id": layerName + "-shadow",
            "type": "line",
            "source": this.modelSourceId,
            "source-layer": "trips",
            "minzoom": this.minZoomTrips,
            "layout": {
                "visibility": "visible",
                "line-cap": "round"
            },
            "paint": {
                "line-width": 0,
                "line-blur": 1,
                "line-color": "#000",
                "line-opacity": 1,
                //"line-translate": [shadowSize/1.8, shadowSize/1.8],
                "line-offset": offsetExpression,
            },
            "filter": ["literal", false]
        }, lowest);
    }
    addVehicleTypeTripLayers(vehicleType) {
        const layerName = `vehicle_${vehicleType}_trips`;
        if (typeof this.map.getLayer(layerName) !== "undefined")
            return;
        // get lowest label and road.
        const style = this.map.getStyle();
        let lowest = undefined;
        for (let lIdx = 0; lIdx < style.layers.length; lIdx++) {
            const layerStyle = style.layers[lIdx];
            if (layerStyle && layerStyle["source"] === "od-points") {
                if (!lowest) {
                    lowest = layerStyle.id;
                }
            }
            if (layerStyle && layerStyle["source-layer"] === "transportation_name") {
                if (!lowest) {
                    lowest = layerStyle.id;
                }
            }
            if (layerStyle && layerStyle["source-layer"] === "locations") {
                if (!lowest) {
                    lowest = layerStyle.id;
                }
            }
        }
        const offsetExpression = [
            "interpolate", ["linear"], ["zoom"],
            13, 0,
            14, 0,
            15, 0,
            16, 0,
            17, ["case", ["get", "_forward"], 12, -12],
            18, ["case", ["get", "_forward"], 14, -14],
        ];
        const textOffsetExpression = [
            "interpolate", ["linear"], ["zoom"],
            12, ["literal", [0, 0]],
            13, ["case", ["get", "_forward"], ["literal", [.25, .25]], ["literal", [-.25, -.25]]],
            14, ["case", ["get", "_forward"], ["literal", [.5, .5]], ["literal", [-.5, -.5]]],
            15, ["case", ["get", "_forward"], ["literal", [.75, .75]], ["literal", [-.75, -.75]]]
        ];
        this.map.addLayer({
            "id": `${layerName}`,
            "type": "line",
            "source": this.modelSourceId,
            "source-layer": "trips",
            "minzoom": this.minZoomTrips,
            "layout": {
                "visibility": "visible",
                "line-cap": "round"
            },
            "paint": {
                "line-width": 0,
                "line-color": "#000",
                "line-offset": offsetExpression,
            },
            "filter": ["literal", false]
        }, lowest);
        this.map.addLayer({
            "id": layerName + "-counts",
            "type": "symbol",
            "source": this.modelSourceId,
            "source-layer": "trips",
            "minzoom": this.MIN_LABELS_ZOOM,
            "layout": {
                "visibility": "visible",
                "text-field": "{trip_count}",
                "text-size": 12,
                "symbol-placement": "line-center",
                "symbol-sort-key": ["-", ["to-number", ["get", "trip_count"]]],
                "text-offset": textOffsetExpression,
                "text-padding": 20
            },
            "paint": {
                "text-color": "#000",
                "text-halo-color": "#ffffff",
                "text-halo-width": 2,
                "text-opacity": 1
            },
            "filter": ["literal", false]
        }, this.locationLabelsLayerId);
    }
}
